import type { MenuItem } from "@replo/design-system/components/menu/Menu";
import type { MetricName } from "schemas/generated/analyticsRead";
import type { Funnel } from "schemas/generated/funnel";

import * as React from "react";

import MetricWithDelta from "@editor/components/analytics/MetricWithDelta";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";

import { useFunnelAnalytics } from "@/features/analytics/useFunnelAnalytics";
import EditableFunnelName from "@/features/funnel/components/EditableFunnelName";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu, MenuTrigger } from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChartLine, Circle, ExternalLink, MoreVertical } from "lucide-react";
import { Link } from "react-router-dom";

import DeleteFunnelModal from "./DeleteFunnelModal";
import PreviewThumbnail from "./PreviewThumbnail";

type FunnelCardMenuProps = {
  setIsDeleteModalOpen: (isOpen: boolean) => void;
  setIsRenamingFunnel: (isOpen: boolean) => void;
};

const FunnelCardMenu: React.FC<FunnelCardMenuProps> = ({
  setIsDeleteModalOpen,
  setIsRenamingFunnel,
}) => {
  const menuItems: MenuItem[] = [
    {
      type: "leaf",
      id: "renameFunnel",
      variant: "default",
      size: "sm",
      title: "Rename",
      onSelect: () => {
        setIsRenamingFunnel(true);
      },
    },
    {
      type: "leaf",
      id: "deleteFunnel",
      variant: "default",
      size: "sm",
      title: "Delete",
      onSelect: () => {
        setIsDeleteModalOpen(true);
      },
    },
  ];

  return (
    <Menu
      align="end"
      size="sm"
      items={menuItems}
      trigger={
        <MenuTrigger asChild>
          <IconButton
            variant="tertiary"
            size="base"
            icon={<MoreVertical size={16} className="text-muted" />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-hidden
          />
        </MenuTrigger>
      }
    />
  );
};

type FunnelCardProps = {
  funnel: Funnel;
  layoutClassName?: string;
};

const FunnelCard: React.FC<FunnelCardProps> = ({ funnel, layoutClassName }) => {
  const { project } = useCurrentProjectContext();
  const { openModal } = useModal();
  const logEvent = useLogAnalytics();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isRenamingFunnel, setIsRenamingFunnel] = React.useState(false);

  if (!project) {
    return null;
  }

  const timeZone =
    project.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="h-full">
      <Link to={`/shops/${project.id}/funnels/${funnel.id}`}>
        <div
          className={twMerge(
            "border-0.5 border-border rounded-lg bg-white hover:bg-menu-item-hover flex flex-col cursor-pointer shadow-sm transition-shadow h-full",
            layoutClassName,
          )}
        >
          <div className="p-3 flex justify-between items-center border-b-0.5 border-border">
            <div className="flex items-center gap-1.5">
              <Circle
                size={8}
                className={twMerge(
                  "fill-current",
                  project.deployStatus === "DEPLOYED"
                    ? "text-active"
                    : "text-muted",
                )}
              />
              <EditableFunnelName
                funnel={funnel}
                projectId={project.id}
                isRenaming={isRenamingFunnel}
                setIsRenaming={setIsRenamingFunnel}
                titleClassName="header-h4 text-default line-clamp-1"
              />
            </div>
            <div className="flex items-center gap-1">
              <Tooltip triggerAsChild content="View funnel analytics">
                <IconButton
                  variant="secondary"
                  size="base"
                  icon={<ChartLine size={16} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openModal({
                      type: "funnelAnalyticsModal",
                      props: { funnel, timeZone },
                    });
                    logEvent("analytics.funnel.analytics.modal.viewed", {
                      funnelId: funnel.id,
                      funnelName: funnel.name,
                      projectId: project.id,
                    });
                  }}
                />
              </Tooltip>
              {project.deployStatus === "DEPLOYED" && (
                <>
                  <IconButton
                    variant="secondary"
                    size="base"
                    icon={<ExternalLink size={16} />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                </>
              )}
              <FunnelCardMenu
                setIsDeleteModalOpen={setIsDeleteModalOpen}
                setIsRenamingFunnel={setIsRenamingFunnel}
              />
            </div>
          </div>

          <div className="p-4 flex flex-col gap-2">
            <div className="flex justify-center gap-4 rounded-b-lg h-[155px] overflow-hidden">
              {funnel.funnelSteps.map(
                (step) =>
                  step.file &&
                  step.file.pageRoute && (
                    <PreviewThumbnail
                      key={step.id}
                      pageRoute={step.file.pageRoute}
                      className="w-[80px] h-[120px]"
                    />
                  ),
              )}
            </div>
            <FunnelAnalytics funnel={funnel} timeZone={timeZone} />
          </div>
        </div>
      </Link>
      <DeleteFunnelModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        funnelId={funnel.id}
        projectId={project.id}
      />
    </div>
  );
};

const FunnelAnalytics: React.FC<{
  funnel: Funnel;
  timeZone: string;
}> = ({ funnel, timeZone }) => {
  const { funnelRoutes, mainRangeResults, queryHasNoData, isLoading } =
    useFunnelAnalytics(funnel, timeZone);

  const lastFunnelRoute = funnelRoutes[funnelRoutes.length - 1];

  const lastFunnelRouteRangeResult = mainRangeResults.find(
    (result) => result.urlPath === lastFunnelRoute,
  );

  const metricsToShow: { key: MetricName; label: string }[] = [
    { key: "unique_sessions", label: "Sessions" },
    { key: "revenue", label: "Revenue" },
    { key: "conversion_rates", label: "Conversion Rate" },
  ];

  if (queryHasNoData && !isLoading) {
    return (
      <InlineAlert variant="info">
        Start driving traffic to view funnel analytics!
      </InlineAlert>
    );
  }

  return (
    <div className="flex flex-row justify-between gap-2">
      {metricsToShow.map((metric) => (
        <div key={metric.key} className="flex flex-col gap-2">
          <span className="text-sm text-muted">{metric.label}</span>
          {isLoading ? (
            <Skeleton className="h-5 w-24" />
          ) : (
            <MetricWithDelta
              name={metric.key}
              value={
                lastFunnelRouteRangeResult?.metrics[metric.key]?.[0] ?? null
              }
              delta={undefined}
              doesCompareValueExist={false}
              wrapperClassName="gap-2"
              valueClassName="text-base font-semibold"
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default FunnelCard;
